import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AccountsLayout from "../AccountsLayout";
import { useStore } from "../../../Components/StateProvider";
import { TbEdit } from "react-icons/tb";
import { IoSettingsOutline } from "react-icons/io5";
import {
  ErrorAlert,
  GetRecords,
  saveRecord,
  SuccessAlert,
} from "../../../functions/api";
import { Draggable, Droppable, DragDropContext } from "react-beautiful-dnd";

const InvoiceSerial = () => {
  const store = useStore();
  const { companyId } = useParams();
  const [columns, setColumns] = useState([
    { id: "module", label: "Module" },
    { id: "org_alias", label: "ORG Alias/Project Type" },
    { id: "prefix", label: "Prefix" },
    { id: "financial_year", label: "Financial Year" },
    { id: "starting_number", label: "Starting Number", static: true },
    { id: "preview", label: "Preview", static: true },
  ]);

  const [data, setData] = useState([]);
  const [backupData, setBackupData] = useState([]);
  const [editMode, setEditMode] = useState(false);

  const generatePreview = (item, columnOrder) => {
    const columnData = columnOrder
      .filter((col) => col.id !== "module" && col.id !== "preview")
      .map((col) => {
        if (col.id === "org_alias") {
          return `${item.org_alias}${item.project_alias}`;
        }
        return item[col.id] ?? "";
      });
    return columnData.filter(Boolean).join("/");
  };

  const handleEdit = () => {
    setBackupData([...data]);
    setEditMode(true);
  };

  const handleChange = (index, field, value) => {
    const updatedData = [...data];
    updatedData[index][field] = value;

    updatedData[index].preview = generatePreview(updatedData[index], columns);

    setData(updatedData);
  };

  const handleSave = () => {
    store.startLoading();
    let saveData = data.map((item) => ({
      ...item,
      modified_by: store.getuser().id,
    }));
    saveRecord(
      `/api/v2/accounting/updatepreferencenumber/${companyId}`,
      saveData
    )
      .then((res) => {
        if (res.success) {
          store.stopLoading();
          SuccessAlert("Preference Updated Successfully!");
        } else {
          store.stopLoading();
          ErrorAlert(
            res?.errormessage?.response?.data?.errorMessage ||
              "Error Found, Please contact the Admin"
          );
        }
      })
      .catch(() => {
        store.stopLoading();
        ErrorAlert("Error Found, Please contact the Admin");
      });

    setEditMode(false);
  };

  const handleCancel = () => {
    setData(backupData);
    setEditMode(false);
  };

  useEffect(() => {
    store.setmodule("configurations", {}).then(() => store.stopLoading());
    GetRecords(`/api/v2/accounting/getpreferencenumber/${companyId}`).then(
      (res) => {
        setData(res.record);
      }
    );
  }, []);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const updatedColumns = Array.from(columns);
    const [moved] = updatedColumns.splice(result.source.index, 1);
    updatedColumns.splice(result.destination.index, 0, moved);

    setColumns(updatedColumns);
    setData((prevData) =>
      prevData.map((item) => ({
        ...item,
        preview: generatePreview(item, updatedColumns),
      }))
    );
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold"></h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1 border rounded-md"
          >
            <TbEdit size={26} /> Edit
          </button>
        </div>
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <table className="w-full border-collapse border border-gray-200">
          <thead className="bg-gray-100">
            <Droppable droppableId="columns" direction="horizontal">
              {(provided) => (
                <tr ref={provided.innerRef} {...provided.droppableProps}>
                  {columns.map((col, index) =>
                    col.static || col.id === "module" ? (
                      <th
                        key={col.id}
                        className="p-2 text-center border border-gray-200"
                      >
                        {col.label}
                      </th>
                    ) : (
                      <Draggable
                        key={col.id}
                        draggableId={col.id}
                        index={index}
                      >
                        {(provided) => (
                          <th
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="p-2 text-center border border-gray-200 cursor-move"
                          >
                            {col.label}
                          </th>
                        )}
                      </Draggable>
                    )
                  )}
                  {provided.placeholder}
                </tr>
              )}
            </Droppable>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                {columns.map((col) => (
                  <td
                    key={col.id}
                    className="p-2 text-center border border-gray-200"
                  >
                    {editMode && col.id !== "module" && col.id !== "starting_number" && col.id !== "preview" ? (
                      col.id === "org_alias" ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={item.org_alias}
                            onChange={(e) =>
                              handleChange(index, "org_alias", e.target.value)
                            }
                            className="w-1/2 border-b  text-center border-gray-400 focus:outline-none focus:border-blue-500"
                          />
                          <input
                            type="text"
                            value={item.project_alias}
                            disabled
                            className="w-1/2 border-b disabled:bg-[#c6c6ca] text-center focus:outline-none focus:border-blue-500 bg-transparent"
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={item[col.id]}
                          onChange={(e) =>
                            handleChange(index, col.id, e.target.value)
                          }
                          className="w-full text-center border-b border-gray-400 focus:outline-none focus:border-blue-500"
                        />
                      )
                    ) : col.id === "org_alias" ? (
                      `${item.org_alias}${item.project_alias}`
                    ) : (
                      item[col.id]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </DragDropContext>
      {editMode && (
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="bg-red-500 text-white px-4 py-2 border rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-primary text-secondary px-4 py-2 border rounded-md"
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceSerial;
